import differenceInDays from 'date-fns/differenceInDays';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import isNil from 'lodash/isNil';
import {v4 as uuidv4} from 'uuid';
import {t} from '../../common/getTranslations';
import {requestContext} from '../../common/requestContext';
import {CreditStatusVo} from '../../common/shared/api/nicolas/vo/enums/CreditStatusVo.enum';
import {KakaoCashReportDataVo} from '../../common/shared/api/nicolas/vo/KakaoCashReportDataVo.interface';
import {SalesTicketVo} from '../../common/shared/api/nicolas/vo/SalesTicketVo.interface';
import {SaleTypeVo} from '../../common/shared/api/nicolas/vo/SaleTypeVo.enum';
import {TicketSalesVo} from '../../common/shared/api/nicolas/vo/TicketSalesVo.interface';
import {TicketTypeVo} from '../../common/shared/api/nicolas/vo/TicketTypeVo.enum';
import {formatWithCommas} from '../../common/shared/utils/formatWithCommas';
import {parseDate} from '../../common/shared/utils/parseDate';
import {getCashChargingExtraLabel} from '../../design-system/organisms/HistoryItem/getCashChargingExtraLabel.function';
import {getSalesTicketPaidText} from '../../design-system/organisms/HistoryItem/getSalesTicketPaidText.function';
import {getSalesTicketTitle} from '../../design-system/organisms/HistoryItem/getSalesTicketTitle.function';
import {getTicketSalesPaidText} from '../../design-system/organisms/HistoryItem/getTicketSalesPaidText.function';
import {getTicketSalesRefundExtraLabel} from '../../design-system/organisms/HistoryItem/getTicketSalesRefundExtraLabel.function';
import {getTicketTitle} from '../../design-system/organisms/HistoryItem/getTicketTitle.function';
import {getUsageSalesTicketType} from '../../design-system/organisms/HistoryItem/getUsageSalesTicketType.function';
import {ExtraLabelType, History, HistoryType} from '../generated/types';

// 캐시 충전 내역
export function createHistoryWithKakaoCashReportDataVo(
  kakaoCashReportData: KakaoCashReportDataVo
): History {
  const remainDays = kakaoCashReportData.expired_at
    ? differenceInDays(parseDate(kakaoCashReportData.expired_at), new Date())
    : undefined;
  const id = uuidv4();
  const isRefundStatus =
    kakaoCashReportData.credit_status === CreditStatusVo.Refund ||
    kakaoCashReportData.credit_status === CreditStatusVo.CancelCharge ||
    kakaoCashReportData.credit_status === CreditStatusVo.CancelIAPMarket ||
    kakaoCashReportData.credit_status ===
      CreditStatusVo.WithdrawByAdminPlayPoints;
  const isExpired = kakaoCashReportData.expired_at
    ? differenceInMilliseconds(
        parseDate(kakaoCashReportData.expired_at),
        new Date()
      ) <= 0
    : false;
  const paymentMethod = kakaoCashReportData.payment_method_translate_key
    ? t(kakaoCashReportData.payment_method_translate_key)
    : kakaoCashReportData.payment_method;
  return {
    id,
    purchasedItem: isNil(kakaoCashReportData.cash_amount) // 빌링에선 필수값이라 했으나 배포 타이밍 이슈로 방어로직 추가(굳이 삭제는 안해도 될듯)
      ? kakaoCashReportData.purchased_item
      : t('common_cash_amount_sp', {
          values: {value: formatWithCommas(kakaoCashReportData.cash_amount)},
        }),
    detailText:
      // event_name은 이벤트 캐시에만 내려오지만 payment_method 는 이벤트, 실충전 관계없이 항상 내려오므로 이벤트 캐시 이름을 먼저 사용합니다.
      kakaoCashReportData.event_name || paymentMethod,
    createDt: kakaoCashReportData.created_at,
    displayDt: kakaoCashReportData.display_date,
    price:
      (kakaoCashReportData.credit_status === CreditStatusVo.Charge ||
        isRefundStatus) &&
      kakaoCashReportData.amount
        ? t('add_cash_won', {
            values: {value: formatWithCommas(kakaoCashReportData.amount)},
          })
        : '',
    priceColor:
      isRefundStatus ||
      kakaoCashReportData.credit_status === CreditStatusVo.WithdrawByAdmin
        ? 'rgb(var(--colors-static-point-red))'
        : undefined,
    extraLabelType: isExpired
      ? ExtraLabelType.Expired
      : getCashChargingExtraLabel(kakaoCashReportData),
    expiredAt:
      kakaoCashReportData.credit_status === CreditStatusVo.EventCash
        ? kakaoCashReportData.expired_at
        : undefined,
    remainDays,
    purchasedItemColor:
      isRefundStatus ||
      kakaoCashReportData.credit_status === CreditStatusVo.WithdrawByAdmin
        ? 'rgb(var(--colors-static-point-red))'
        : undefined,
    creditMasterId: kakaoCashReportData.credit_master_id
      ? String(kakaoCashReportData.credit_master_id)
      : undefined,
    historyType: HistoryType.CashCharging,
  };
}

export function createHistoryWithSalesTicketVo(
  salesTicket: SalesTicketVo,
  type: HistoryType
): History {
  // 캐시 사용 내역
  if (type === HistoryType.CashUsage) {
    const isCashType = [
      TicketTypeVo.BuyCashDiscountSingle,
      TicketTypeVo.BuyCashSingle,
      TicketTypeVo.RentCashDiscountSingle,
      TicketTypeVo.RentCashSingle,
      TicketTypeVo.BuySet,
      TicketTypeVo.RentSet,
    ].includes(salesTicket.ticket_type);

    const isSet = [TicketTypeVo.BuySet, TicketTypeVo.RentSet].includes(
      salesTicket.ticket_type
    );
    // 태국어의 경우 충전/충전취소가 문구 가장 앞으로 가야해서 분기를 위한 구분값 추가
    const isTh = requestContext.requestInfo.lang.deviceLang === 'th';

    let detailText2;

    if (isCashType && salesTicket.title) {
      if (isSet) {
        detailText2 = salesTicket.title;
      } else {
        detailText2 = `${salesTicket.title} ${getUsageSalesTicketType({
          ticketType: salesTicket.ticket_type,
          itemCount:
            salesTicket.sale_type === SaleTypeVo.Sale
              ? salesTicket.item_count
              : Math.abs(salesTicket.paid_num),
          qty: salesTicket.qty,
        })}`;
      }
    } else {
      const saleTypeText =
        salesTicket.sale_type === SaleTypeVo.Sale
          ? t('cash_history_purchased_cash_2')
          : t('cash_history_purchased_cancel_2');
      const usageText = getUsageSalesTicketType({
        ticketType: salesTicket.ticket_type,
        itemCount:
          salesTicket.sale_type === SaleTypeVo.Sale
            ? salesTicket.item_count
            : Math.abs(salesTicket.paid_num),
        qty: salesTicket.qty,
      });

      detailText2 = isTh
        ? `${saleTypeText} ${usageText}`
        : `${usageText} ${saleTypeText}`;
    }

    return {
      id: salesTicket.uid,
      purchasedItem: t('common_cash_amount_sp', {
        values: {value: getSalesTicketPaidText(salesTicket)},
      }),
      detailText: `${salesTicket.series_title}`,
      detailText2,
      createDt: salesTicket.create_dt,
      purchasedItemColor:
        salesTicket.sale_type === SaleTypeVo.Refund
          ? 'rgb(var(--colors-static-point-red))'
          : undefined, // s-red
      historyType: type,
    };
  }

  // 이용권 충전 내역
  const remainDays = salesTicket.ticket_expire_dt
    ? differenceInDays(parseDate(salesTicket.ticket_expire_dt), new Date())
    : undefined;
  const isExpired = salesTicket.ticket_expire_dt
    ? differenceInMilliseconds(
        parseDate(salesTicket.ticket_expire_dt),
        new Date()
      ) <= 0
    : false;
  return {
    id: salesTicket.uid,
    seriesId: salesTicket.series_id,
    purchasedItem: salesTicket.series_title,
    detailText: getSalesTicketTitle(salesTicket),
    createDt: salesTicket.create_dt,
    price: t('common_cash_amount', {
      values: {value: getSalesTicketPaidText(salesTicket)},
    }),
    extraLabelType:
      salesTicket.sale_type === SaleTypeVo.Refund
        ? ExtraLabelType.RefundTicket
        : isExpired
          ? ExtraLabelType.Expired
          : undefined,
    expiredAt:
      salesTicket.sale_type === SaleTypeVo.Sale && !isExpired
        ? salesTicket.ticket_expire_dt
        : undefined,
    remainDays,
    priceColor:
      salesTicket.sale_type === SaleTypeVo.Refund
        ? 'rgb(var(--colors-static-point-red))'
        : undefined,
    historyType: type,
  };
}

// 이용권 사용 내역
export function createHistoryWithTicketSalesVo(
  ticketSales: TicketSalesVo
): History {
  return {
    id: ticketSales.uid,
    seriesId: ticketSales.series_id,
    purchasedItem: ticketSales.series_title,
    detailText: ticketSales.single_title,
    createDt: ticketSales.create_dt,
    productType:
      ticketSales?.ticket_type !== undefined // 이용권 내역 사용 내역에는 성능 이슈로 ticket_type 이 내려오지 않을 수 있습니다. 그 경우 product_type 으로 만든 값을 보여줍니다
        ? getTicketTitle(ticketSales.ticket_type)
        : getTicketSalesPaidText(ticketSales),
    extraLabelType: getTicketSalesRefundExtraLabel(ticketSales),
    priceColor: 'rgb(var(--colors-theme-solid-100))', // el-bc → theme-solid-100
    historyType: HistoryType.CouponUsage,
  };
}
